import { z } from 'zod';
import { assessmentWithContentSchema } from './assessments.js';
import { DEPRECATED_pagingSchemaWithMaxPageSize, filteringSchemaFrom, pagingSchema, typeaheadFilterSetSchema } from './modifier.js';
import { StudentYear } from './student.js';
import { paginatedResponse } from './util-schema-types.js';
export { lessonKindSchema, LessonKindToLabel } from './lesson-shared.js';
export type { LessonKind } from './lesson-shared.js';

/** Empty CSV cells parse as ""; missing columns become undefined — coerce both to "". */
const csvString = z
  .string()
  .optional()
  .nullable()
  .transform((val) => val ?? '');

/**
 * Empty / missing CSV cells → null. `"0"` and `0` stay 0 (do not treat as falsy unset).
 * Uses parseInt/parseFloat so values like `"50%"` still parse, matching prior import behavior.
 */
function csvOptionalNumber(parse: (raw: string) => number) {
  return z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform((val, ctx): number | null => {
      if (val === null || val === undefined) {
        return null;
      }
      if (typeof val === 'number') {
        if (!Number.isFinite(val)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a number' });
          return z.NEVER;
        }
        return val;
      }
      const trimmed = val.trim();
      if (trimmed === '') {
        return null;
      }
      const parsed = parse(trimmed);
      if (!Number.isFinite(parsed)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a number' });
        return z.NEVER;
      }
      return parsed;
    });
}

const csvOptionalInt = csvOptionalNumber((raw) => Number.parseInt(raw, 10));
const csvOptionalFloat = csvOptionalNumber((raw) => Number.parseFloat(raw));

/** Blank number inputs would otherwise coerce to 0; treat blank as unset so 0 is a real value. */
export const optionalFormNumber = (schema: z.ZodNumber) =>
  z.preprocess((val) => (val === '' || val === null || val === undefined ? undefined : val), schema.optional());

export const lessonSchema = z.object({
  id: z.string(),
  name: z.string(),
  objectives: z.string().array(),
  handouts: z.string().array(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  shouldSkipFeedback: z.boolean().nullable(),
  minWPM: z.number().nullable(),
  maxWPM: z.number().nullable(),
  minAccuracyPercent: z.number().nullable(),
  maxAccuracyPercent: z.number().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});

export type Lesson = z.infer<typeof lessonSchema>;

export const lessonWithAssessmentsSchema = lessonSchema.extend({
  assessments: z.array(z.lazy(() => assessmentWithContentSchema)),
  /** Learning path names that include this lesson (via `LearningPathLesson`). Used e.g. for fluency detection on sessions. */
  parentLearningPathNames: z.array(z.string()),
});
export type LessonWithAssessments = z.infer<typeof lessonWithAssessmentsSchema>;

export const createLessonOptionsSchema = z.object({
  name: z.string(),
  objectives: z.string().array().nullable(),
  handouts: z.string().array().nullable(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  shouldSkipFeedback: z.boolean().nullable(),
  assessmentIds: z.string().array().nullable(),
  assessmentContentSelectionOverride: z
    .array(z.object({ assessmentId: z.string(), assessmentContentId: z.string() }))
    .optional()
    .nullable(),
  minWPM: z.number().nullable(),
  maxWPM: z.number().nullable(),
  minAccuracyPercent: z.number().nullable(),
  maxAccuracyPercent: z.number().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});
export type CreateLessonOptions = z.infer<typeof createLessonOptionsSchema>;

export const createLessonCsvRow = z.object({
  lessonName: z
    .string({ required_error: 'lessonName column is required' })
    .min(1, { message: 'lessonName must not be empty' }),
  Handouts: csvString,
  'Learning objectives': csvString,
  'Lesson link': csvString,
  'Phonemic Awareness - Can the student blend all sounds?': csvString,
  'Phonemic Awareness - Can the student segment all words?': csvString,
  'Phonemic Awareness - Can the student isolate all sounds?': csvString,
  'Phonics Skill 1': csvString,
  'Phonics Skill 2': csvString,
  'Phonics Skill 3': csvString,
  'Morpheme Skill 1': csvString,
  'Morpheme Skill 2': csvString,
  'Comprehension Skill 1': csvString,
  'Comprehension Skill 2': csvString,
  'Fluency Skill': csvString,
  'Comprehending Text Skill': csvString,
  'Progress Monitor Sounds': csvString,
  'Progress Monitor Words': csvString,
  'Progress Monitor Text': csvString,
  'Progress Monitor Letter Names': csvString,
  'Grade Level Assessment': csvString,
  Diagnostic: csvString,
  'No feedback needed': csvString,
  '# Min WPM': csvOptionalInt,
  '# Max WPM': csvOptionalInt,
  'Min Accuracy': csvOptionalFloat,
  'Max Accuracy': csvOptionalFloat,
  'Florida standards': csvString,
  'Arkansas standards': csvString,
});
export type CreateLessonCsvRow = z.infer<typeof createLessonCsvRow>;

export const CreateLessonCsvFormKeys = {
  csvFile: 'csvFile',
} as const;

export const updateLessonOptionsSchema = z.object({
  name: z.string(),
  objectives: z.string().array().nullable(),
  handouts: z.string().array().nullable(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  assessmentIds: z.string().array().nullable(),
  shouldSkipFeedback: z.boolean().optional().nullable(),
  minWPM: z.number().optional().nullable(),
  maxWPM: z.number().optional().nullable(),
  minAccuracyPercent: z.number().optional().nullable(),
  maxAccuracyPercent: z.number().optional().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});
export type UpdateLessonOptions = z.infer<typeof updateLessonOptionsSchema>;

export const getLessonsQuerySchema = z.object({
  query: z.string().optional(),
});

export type GetLessonsQuery = z.infer<typeof getLessonsQuerySchema>;

export const getLessonsPaginatedQuerySchema = DEPRECATED_pagingSchemaWithMaxPageSize.extend(getLessonsQuerySchema.shape);
export type GetLessonsPaginatedQuery = z.infer<typeof getLessonsPaginatedQuerySchema>;

export const getLessonResultsSchema = paginatedResponse.extend({
  lessons: z.array(lessonWithAssessmentsSchema),
});

export type GetLessonResults = z.infer<typeof getLessonResultsSchema>;

export const getLessonsTypeaheadFilterSchema = typeaheadFilterSetSchema;
export type GetLessonsTypeaheadFilter = z.infer<typeof getLessonsTypeaheadFilterSchema>;

export const getLessonsTypeaheadModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getLessonsTypeaheadFilterSchema),
);
export type GetLessonsTypeaheadModifier = z.infer<typeof getLessonsTypeaheadModifierSchema>;

export const getLessonsTypeaheadResultsSchema = z.array(lessonSchema);
export type GetLessonsTypeaheadResults = z.infer<typeof getLessonsTypeaheadResultsSchema>;

export const getLessonLinkBasedOnStudentYear = (
  lessonWithLinks: Pick<Lesson, 'lessonLinkKTo2' | 'lessonLink3To5' | 'lessonLink6To8'>,
  studentYearToCheck: StudentYear | null | undefined,
): string | null => {
  if (
    studentYearToCheck === 'Pre_K' || // Pre-K also uses K-2 link
    studentYearToCheck === 'K' ||
    studentYearToCheck === 'first' ||
    studentYearToCheck === 'second'
  ) {
    return lessonWithLinks.lessonLinkKTo2;
  }
  if (studentYearToCheck === 'third' || studentYearToCheck === 'fourth' || studentYearToCheck === 'fifth') {
    return lessonWithLinks.lessonLink3To5;
  }
  if (studentYearToCheck === 'sixth' || studentYearToCheck === 'seventh' || studentYearToCheck === 'eighth') {
    return lessonWithLinks.lessonLink6To8;
  }

  // Default to Lesson link 3-5 for grade 9 - 13 or grade is empty
  return lessonWithLinks.lessonLink3To5;
};

export const isGLALesson = (lessonName: string | null | undefined) => {
  if (!lessonName) return false;
  return lessonName.includes('(GLA)');
};
